import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, Calculator, TrendingUp, AlertCircle, CheckCircle2, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";

const offerSchema = z.object({
  baseSalary: z.string().min(1, "Base salary is required"),
  location: z.string().min(1, "Location is required"),
});

interface OfferAnalysis {
  compensation: {
    baseSalary: number;
    signingBonus: number;
    annualBonus: number;
    equityAnnual: number;
    equityTotal: number;
    benefitsValue: number;
    year1Total: number;
    year4Total: number;
    year4Average: number;
  };
  breakdown: {
    baseSalaryPercent: string;
    bonusPercent: string;
    equityPercent: string;
    benefitsPercent: string;
  };
  analysis: {
    verdict: 'excellent' | 'good' | 'fair' | 'below_market';
    summary: string;
    strengths: string[];
    concerns: string[];
    recommendation: 'accept' | 'negotiate_salary' | 'negotiate_benefits' | 'decline';
    negotiationPoints: string[];
    marketComparison: string;
  };
  marketComparison?: {
    difference: number;
    percentDifference: number;
    position: 'above' | 'below';
  };
}

export function OfferEvaluator() {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<OfferAnalysis | null>(null);

  // Form state
  const [baseSalary, setBaseSalary] = useState("");
  const [signingBonus, setSigningBonus] = useState("");
  const [annualBonus, setAnnualBonus] = useState("");
  const [annualBonusType, setAnnualBonusType] = useState("amount");
  const [location, setLocation] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  // Equity state
  const [hasEquity, setHasEquity] = useState(false);
  const [equityType, setEquityType] = useState("rsu");
  const [equityAmount, setEquityAmount] = useState("");
  const [equityCurrentValue, setEquityCurrentValue] = useState("");
  const [equityStrikePrice, setEquityStrikePrice] = useState("");
  const [vestingYears, setVestingYears] = useState("4");

  // Benefits state
  const [hasHealthInsurance, setHasHealthInsurance] = useState(false);
  const [healthContribution, setHealthContribution] = useState("80");
  const [has401k, setHas401k] = useState(false);
  const [match401k, setMatch401k] = useState("3");
  const [ptoDays, setPtoDays] = useState("");

  const handleAnalyze = async () => {
    try {
      const validated = offerSchema.parse({ baseSalary, location });

      setLoading(true);

      const equity = hasEquity ? {
        type: equityType,
        amount: equityAmount,
        currentValue: equityCurrentValue,
        strikePrice: equityStrikePrice,
        vestingYears,
      } : null;

      const benefits = {
        healthInsurance: hasHealthInsurance,
        healthInsuranceContribution: healthContribution,
        retirement401k: has401k,
        retirement401kMatch: match401k,
        ptoDays: ptoDays,
      };

      // Generate context hash for idempotency
      const encoder = new TextEncoder();
      const contextData = `${validated.baseSalary}:${validated.location}:${jobTitle}`;
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(contextData));
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const contextHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
      const refId = `salary:offer:${contextHash}:${Date.now()}`;

      // Phase 1: Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'salary.offer_evaluation', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast.error(errorData.message || 'Insufficient credits');
          return;
        }
        throw creditError;
      }

      // Phase 2: Analyze offer
      const { data, error } = await supabase.functions.invoke('analyze-offer', {
        body: {
          baseSalary: validated.baseSalary,
          signingBonus,
          annualBonus,
          annualBonusType,
          equity,
          benefits,
          location: validated.location,
          jobTitle: jobTitle || "Position",
        },
      });

      if (error) throw error;

      setAnalysis(data);
      
      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`Offer analyzed! Used ${creditResult.cost} credits. ${creditResult.remaining} remaining.`);
      } else {
        toast.success("Offer analyzed successfully!");
      }

      // Refresh credits display
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
      } else {
        console.error("[SALARY-OFFER] Error:", error);
        toast.error("Failed to analyze offer. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getVerdictColor = (verdict: string) => {
    switch (verdict) {
      case 'excellent':
        return 'bg-green-500';
      case 'good':
        return 'bg-blue-500';
      case 'fair':
        return 'bg-yellow-500';
      case 'below_market':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getVerdictIcon = (verdict: string) => {
    switch (verdict) {
      case 'excellent':
      case 'good':
        return <CheckCircle2 className="h-8 w-8" />;
      case 'fair':
        return <Info className="h-8 w-8" />;
      case 'below_market':
        return <AlertCircle className="h-8 w-8" />;
      default:
        return <Info className="h-8 w-8" />;
    }
  };

  if (analysis) {
    const { compensation, breakdown, analysis: aiAnalysis, marketComparison } = analysis;

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Offer Analysis Results</h2>
            <p className="text-muted-foreground">Comprehensive breakdown and recommendation</p>
          </div>
          <Button variant="outline" onClick={() => setAnalysis(null)}>
            Evaluate New Offer
          </Button>
        </div>

        {/* Verdict Card */}
        <Card className={`border-2 ${getVerdictColor(aiAnalysis.verdict)}`}>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className={`p-4 rounded-full ${getVerdictColor(aiAnalysis.verdict)} text-white flex-shrink-0`}>
                {getVerdictIcon(aiAnalysis.verdict)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="text-xl sm:text-2xl font-bold capitalize break-words">{aiAnalysis.verdict.replace('_', ' ')} Offer</h3>
                  <Badge variant="secondary" className="capitalize truncate max-w-[200px]">
                    {aiAnalysis.recommendation.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <p className="text-base sm:text-lg break-words">{aiAnalysis.summary}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Compensation */}
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Year 1 Total Compensation</CardTitle>
              <CardDescription>All components combined</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold mb-4">
                ${compensation.year1Total.toLocaleString()}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base Salary</span>
                  <span className="font-medium">${compensation.baseSalary.toLocaleString()}</span>
                </div>
                {compensation.signingBonus > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Signing Bonus</span>
                    <span className="font-medium">${compensation.signingBonus.toLocaleString()}</span>
                  </div>
                )}
                {compensation.annualBonus > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Annual Bonus</span>
                    <span className="font-medium">${compensation.annualBonus.toLocaleString()}</span>
                  </div>
                )}
                {compensation.equityAnnual > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Equity (Annual)</span>
                    <span className="font-medium">${compensation.equityAnnual.toLocaleString()}</span>
                  </div>
                )}
                {compensation.benefitsValue > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Benefits Value</span>
                    <span className="font-medium">${compensation.benefitsValue.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4-Year Total Value</CardTitle>
              <CardDescription>Including all vested equity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold mb-2">
                ${compensation.year4Total.toLocaleString()}
              </div>
              <div className="text-muted-foreground mb-4">
                Avg: ${compensation.year4Average.toLocaleString()}/year
              </div>
              {compensation.equityTotal > 0 && (
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span>Total Equity Value</span>
                    <span className="font-medium">${compensation.equityTotal.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Market Comparison */}
        {marketComparison && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <TrendingUp className={`h-8 w-8 ${marketComparison.position === 'above' ? 'text-green-500' : 'text-orange-500'}`} />
                <div>
                  <div className="text-sm text-muted-foreground">Compared to Market Average</div>
                  <div className="text-2xl font-bold">
                    {marketComparison.position === 'above' ? '+' : ''}
                    {marketComparison.percentDifference.toFixed(1)}%
                  </div>
                  <div className="text-sm text-muted-foreground">
                    ${Math.abs(marketComparison.difference).toLocaleString()} {marketComparison.position} market
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Compensation Breakdown Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Compensation Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2 text-sm">
                  <span>Base Salary</span>
                  <span className="font-medium">{breakdown.baseSalaryPercent}%</span>
                </div>
                <div className="h-4 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500"
                    style={{ width: `${breakdown.baseSalaryPercent}%` }}
                  />
                </div>
              </div>
              {parseFloat(breakdown.bonusPercent) > 0 && (
                <div>
                  <div className="flex justify-between mb-2 text-sm">
                    <span>Bonuses</span>
                    <span className="font-medium">{breakdown.bonusPercent}%</span>
                  </div>
                  <div className="h-4 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500"
                      style={{ width: `${breakdown.bonusPercent}%` }}
                    />
                  </div>
                </div>
              )}
              {parseFloat(breakdown.equityPercent) > 0 && (
                <div>
                  <div className="flex justify-between mb-2 text-sm">
                    <span>Equity</span>
                    <span className="font-medium">{breakdown.equityPercent}%</span>
                  </div>
                  <div className="h-4 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500"
                      style={{ width: `${breakdown.equityPercent}%` }}
                    />
                  </div>
                </div>
              )}
              {parseFloat(breakdown.benefitsPercent) > 0 && (
                <div>
                  <div className="flex justify-between mb-2 text-sm">
                    <span>Benefits</span>
                    <span className="font-medium">{breakdown.benefitsPercent}%</span>
                  </div>
                  <div className="h-4 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-500"
                      style={{ width: `${breakdown.benefitsPercent}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* AI Analysis */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Strengths
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {aiAnalysis.strengths.map((strength, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                Concerns
              </CardTitle>
            </CardHeader>
            <CardContent>
              {aiAnalysis.concerns.length > 0 ? (
                <ul className="space-y-2">
                  {aiAnalysis.concerns.map((concern, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-orange-500 mt-1">•</span>
                      <span>{concern}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">No major concerns identified</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Negotiation Points */}
        {aiAnalysis.negotiationPoints.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Negotiation Points</CardTitle>
              <CardDescription>Key areas to discuss with the employer</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {aiAnalysis.negotiationPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-primary mt-1">→</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Evaluate Job Offer</CardTitle>
        <CardDescription>
          Calculate total compensation and get AI-powered analysis
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Info */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="jobTitle">Job Title</Label>
            <Input
              id="jobTitle"
              placeholder="e.g., Senior Software Engineer"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location *</Label>
            <Input
              id="location"
              placeholder="e.g., San Francisco, CA"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
        </div>

        {/* Salary & Bonuses */}
        <div>
          <h3 className="font-semibold mb-4">Compensation Details</h3>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="baseSalary">Base Salary *</Label>
              <Input
                id="baseSalary"
                type="number"
                placeholder="e.g., 120000"
                value={baseSalary}
                onChange={(e) => setBaseSalary(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="signingBonus">Signing Bonus</Label>
              <Input
                id="signingBonus"
                type="number"
                placeholder="e.g., 10000"
                value={signingBonus}
                onChange={(e) => setSigningBonus(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="annualBonus">Annual Bonus</Label>
              <Input
                id="annualBonus"
                type="number"
                placeholder={annualBonusType === 'percentage' ? "e.g., 15" : "e.g., 20000"}
                value={annualBonus}
                onChange={(e) => setAnnualBonus(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="annualBonusType">Bonus Type</Label>
              <Select value={annualBonusType} onValueChange={setAnnualBonusType}>
                <SelectTrigger id="annualBonusType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="amount">Fixed Amount ($)</SelectItem>
                  <SelectItem value="percentage">Percentage of Base (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Equity */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Checkbox
              id="hasEquity"
              checked={hasEquity}
              onCheckedChange={(checked) => setHasEquity(checked as boolean)}
            />
            <Label htmlFor="hasEquity" className="font-semibold cursor-pointer">
              Include Equity/Stock Options
            </Label>
          </div>

          {hasEquity && (
            <div className="grid gap-6 md:grid-cols-2 ml-6">
              <div className="space-y-2">
                <Label htmlFor="equityType">Equity Type</Label>
                <Select value={equityType} onValueChange={setEquityType}>
                  <SelectTrigger id="equityType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rsu">RSUs (Restricted Stock Units)</SelectItem>
                    <SelectItem value="options">Stock Options</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="equityAmount">Number of Shares/Units</Label>
                <Input
                  id="equityAmount"
                  type="number"
                  placeholder="e.g., 5000"
                  value={equityAmount}
                  onChange={(e) => setEquityAmount(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="equityCurrentValue">
                  {equityType === 'rsu' ? 'Current Share Price ($)' : 'Current FMV ($)'}
                </Label>
                <Input
                  id="equityCurrentValue"
                  type="number"
                  placeholder="e.g., 100"
                  value={equityCurrentValue}
                  onChange={(e) => setEquityCurrentValue(e.target.value)}
                />
              </div>

              {equityType === 'options' && (
                <div className="space-y-2">
                  <Label htmlFor="equityStrikePrice">Strike Price ($)</Label>
                  <Input
                    id="equityStrikePrice"
                    type="number"
                    placeholder="e.g., 50"
                    value={equityStrikePrice}
                    onChange={(e) => setEquityStrikePrice(e.target.value)}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="vestingYears">Vesting Period (Years)</Label>
                <Select value={vestingYears} onValueChange={setVestingYears}>
                  <SelectTrigger id="vestingYears">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 years</SelectItem>
                    <SelectItem value="3">3 years</SelectItem>
                    <SelectItem value="4">4 years</SelectItem>
                    <SelectItem value="5">5 years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Benefits */}
        <div>
          <h3 className="font-semibold mb-4">Benefits Package</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hasHealthInsurance"
                checked={hasHealthInsurance}
                onCheckedChange={(checked) => setHasHealthInsurance(checked as boolean)}
              />
              <Label htmlFor="hasHealthInsurance" className="cursor-pointer">
                Health Insurance
              </Label>
            </div>

            {hasHealthInsurance && (
              <div className="ml-6 space-y-2">
                <Label htmlFor="healthContribution">Employer Contribution (%)</Label>
                <Input
                  id="healthContribution"
                  type="number"
                  placeholder="e.g., 80"
                  value={healthContribution}
                  onChange={(e) => setHealthContribution(e.target.value)}
                  max={100}
                />
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="has401k"
                checked={has401k}
                onCheckedChange={(checked) => setHas401k(checked as boolean)}
              />
              <Label htmlFor="has401k" className="cursor-pointer">
                401(k) Matching
              </Label>
            </div>

            {has401k && (
              <div className="ml-6 space-y-2">
                <Label htmlFor="match401k">Match Percentage (%)</Label>
                <Input
                  id="match401k"
                  type="number"
                  placeholder="e.g., 3"
                  value={match401k}
                  onChange={(e) => setMatch401k(e.target.value)}
                  max={100}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="ptoDays">PTO Days per Year</Label>
              <Input
                id="ptoDays"
                type="number"
                placeholder="e.g., 20"
                value={ptoDays}
                onChange={(e) => setPtoDays(e.target.value)}
              />
            </div>
          </div>
        </div>

        <Button 
          onClick={handleAnalyze} 
          disabled={loading || !baseSalary || !location}
          className="w-full"
          size="lg"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Analyzing Offer...
            </>
          ) : (
            <>
              <Calculator className="mr-2 h-5 w-5" />
              Analyze Offer
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
